import { useEffect, useRef, useState } from "react";
import axios from "axios";
import classNames from "classnames";
import Header from "../components/Header";
import Card from "../components/Card";
import styles from "./css/Choose.module.css";
import { UserInfo } from "../interfaces/chat.interface";

interface ChooseProps {
  setPage: React.Dispatch<React.SetStateAction<number>>;
  loginId: number;
  setLoginId: React.Dispatch<React.SetStateAction<number>>;
  setSelectedCharacter: React.Dispatch<React.SetStateAction<UserInfo>>;
}

interface Character {
  character_name: string;
  world_name: string;
  character_class: string;
  character_level: number;
  character_image: string;
}

export default function Choose({
  setPage,
  loginId,
  setLoginId,
  setSelectedCharacter,
}: ChooseProps) {
  const [characters, setCharacters] = useState([]);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCharacterSelect = (character: Character) => {
    const convertedCharacter: UserInfo = {
      userId: loginId,
      nickname: character.character_name,
      characterImageUrl: character.character_image,
    };

    setSelectedCharacter(convertedCharacter);
    setPage(2);
  };

  useEffect(() => {
    // 캐싱 데이터 확인
    const fetchCharacters = async () => {
      if (!loginId) {
        sessionStorage.removeItem("id");
        sessionStorage.removeItem("idData");
        sessionStorage.removeItem("charactersData");
        setPage(0);
        return;
      }

      try {
        const idData = Number(sessionStorage.getItem("idData")) || -1;
        const charactersData = JSON.parse(
          sessionStorage.getItem("charactersData") || "{}"
        );

        // 로그인 한 유저와 저장된 유저가 같고, 캐릭터 정보가 이미 있으면 그대로 사용
        if (idData && loginId === idData && charactersData.length) {
          setCharacters(charactersData);
          return;
        }

        // 새로 로그인 한 유저라면 캐릭터 정보 불러오기
        const response = await axios.get(`/api/characters?id=${loginId}`);

        if (response.status === 200) {
          setCharacters(response.data);
          sessionStorage.setItem("idData", loginId.toString());
          sessionStorage.setItem(
            "charactersData",
            JSON.stringify(response.data)
          );
        }
      } catch (error) {
        console.error("캐릭터 정보를 불러오는데 실패했습니다:", error);
      }
    };

    fetchCharacters();

    // 스크롤바 숨김 타이머
    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      setIsScrolling(true);

      // 이전 타이머 취소
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // 새로운 타이머 설정
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000); // 1초 후에 스크롤바 숨김
    };

    content.addEventListener("scroll", handleScroll);

    return () => {
      content.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [setPage, loginId, setLoginId]);

  return (
    <div className={styles.container}>
      <Header type="choose" />
      <div
        ref={contentRef}
        className={classNames(styles.content, {
          [styles.scrolling]: isScrolling,
        })}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.cardGrid}>
            {characters.map((character, index) => (
              <Card
                key={`card-${index}`}
                character={character}
                onClick={() => handleCharacterSelect(character)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
